/**
 * Press Management Page related utils
 */
import type { MultiDataFilterGroup } from "components/shared/MultiDataFilters/utils";
import { formatNumber } from "helpers/utils";
import { t } from "i18next";
import { AgreementStatus } from "store/agreementsStore";
import type { IStats } from "store/managementStore";
import type { TPortalType } from "store/types";

/**
 * Get formatted utils data for Press Management page
 * @param stats Stats for block cards
 * @param publisherType Publisher type "press" or "audiovisual"
 * @param company uses agreements validation chain
 *
 */
export const usePressManagementUtilsData = (
    stats: IStats | null,
    publisherType: TPortalType,
    usesAgreementValidation: boolean,
) => {
    /**
     * Data for stats block cards
     */
    const blockCardsData = [
        {
            unit: stats?.nbTitles ?? 0,
            label: t(`PressDashboard.OngoingPublications_${publisherType}`, {
                count: stats?.nbTitles ?? 0,
            }).toLocaleLowerCase(),
        },
        {
            unit: stats?.nbUnpaidArticles ?? 0,
            label: t(`PressDashboard.UnpaidArticles_${publisherType}`, {
                count: stats?.nbUnpaidArticles ?? 0,
            }).toLocaleLowerCase(),
        },
        {
            unit: formatNumber((stats?.netRemainingToPay ?? 0) / 100, {
                isCurrency: true,
                always2Digit: true,
            }),
            label: t("PressDashboard.NetRemainingToPay").toLocaleLowerCase(),
        },
    ];

    /**
     * Get options for 1st filter button at the right of the searchbar
     */
    const orderOptions = [
        {
            name: "createdAt",
            label: t("PressAudio.Management.CreationDate"),
        },
        { name: "titleName", label: t(`PressAudio.Management.Publication_${publisherType}`) },
        { name: "name", label: t(`PressAudio.Management.Article_${publisherType}`) },
        { name: "beneficiaryName", label: t("PressAudio.Management.Beneficiary") },
    ];

    const signedStatusOptions = [
        { label: t("PressAudio.Management.All"), value: "all", defaultChecked: true },
        { label: t("PressAudio.Management.ToSend"), value: "tosend" },
        { label: t("PressAudio.Management.Pending"), value: "sent" },
        { label: t("PressAudio.Management.Signed"), value: "signed" },
    ];

    const agreementStatusOptions = [
        { label: t("PressAudio.Management.All"), value: "all", defaultChecked: true },
        { label: t("PressAudio.Management.ToSend"), value: "tosend" }, // verified on contractSigned value
        { label: t("ContractValidation.Statusdraft"), value: AgreementStatus.DRAFT },
        { label: t("ContractValidation.Statusnegotiating"), value: AgreementStatus.NEGOTIATING },
        { label: t("ContractValidation.Statussent"), value: AgreementStatus.SENT },
        { label: t("PressAudio.Management.Signed"), value: "signed" }, // verified on contractSigned value
    ];

    /**
     * Get options for 2nd filter button at the right of the searchbar
     */
    const filtersGroups: MultiDataFilterGroup[] = [
        // Signature status options
        {
            label: t("PressAudio.Management.ContractStatus"),
            options: usesAgreementValidation ? agreementStatusOptions : signedStatusOptions,
        },
        // Payment status options
        {
            label: t("PressAudio.Management.PaymentStatus"),
            options: [
                { label: t("PressAudio.Management.All"), value: "all", defaultChecked: true },
                { label: t("Articles.NotPaid"), value: "unpaid" },
                { label: t("Articles.ToPay"), value: "topay" },
                { label: t("Articles.Paid"), value: "paid" },
            ],
        },
    ];

    return { blockCardsData, orderOptions, filtersGroups };
};
